import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_USER } from 'constants/AuthConstant';
import authClient from 'auth/authClient';
import { TEXTO_POR_CODIGO, mensajeDeError } from 'auth/authMessages';

// El token vive en memoria dentro de auth/authClient.js y NO se rehidrata de
// localStorage: al arrancar, App espera authClient.boot() (auth/sesionRedux.js)
// y, si la cookie de refresh vale, adoptarSesion pone token y user. `token`
// aquí es solo la bandera "hay sesión" que leen layouts/index.js,
// ProtectedRoute y PublicRoute; para el header se usa
// authClient.getAccessToken() (lo vigila una regla de ESLint).
//
// `user` sigue guardándose en localStorage (no es credencial) para que
// NavProfile y los lectores de 'user' (useAccess, Checks, OpenReplay) lo
// tengan tras un F5 antes de que /session responda.
//
// El usuario que devuelve /v3/auth/session trae `id`, pero media app lee
// `user.userID` (financiamiento, validación de sociedades): es el mismo hex,
// así que se guardan los dos nombres. Lo guardado por la versión anterior
// traía además el `token` del login viejo; al normalizar aquí se suelta.
function normalizarUsuario(u) {
  return {
    id: u.id ?? u.userID,
    userID: u.userID ?? u.id,
    name: u.name,
    email: u.email,
    job: u.job,
  };
}

function getStoredUser() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const guardado = JSON.parse(localStorage.getItem(AUTH_USER));
    if (!guardado) return {};
    const limpio = normalizarUsuario(guardado);
    // Normalizar solo en memoria no basta: el `token` de 12 h que guardaba la
    // versión anterior seguiría en localStorage hasta el siguiente login. Se
    // reescribe el objeto ya limpio. Si el storage falla (lleno o bloqueado)
    // el usuario en memoria ya va sin token: no se pierde la rehidratación.
    if (guardado.token !== undefined) {
      try {
        localStorage.setItem(AUTH_USER, JSON.stringify(limpio));
      } catch (e) {
        /* storage bloqueado: se reintentará en el próximo arranque */
      }
    }
    return limpio;
  } catch {
    return {};
  }
}

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  redirect: '',
  token: null,
  user: getStoredUser(),
  booting: true,
  // Contador de avisos. Sube con cada aviso nuevo, aunque el texto sea el
  // mismo: LoginForm lo usa como dependencia del temporizador que esconde el
  // mensaje, y sin él dos errores idénticos seguidos (contraseña mal dos veces)
  // no rearmaban el temporizador y el segundo aviso se iba casi al instante.
  messageId: 0,
};

export function usuarioDeSesion(datos) {
  return normalizarUsuario((datos && datos.user) || {});
}

// mensajeDeError saca del AuthError lo que el server sí dijo (`serverMessage`):
// `AuthError.message` cae al `code` cuando no hubo texto, y BLACKLISTED acabaría
// mostrando la palabra "BLACKLISTED".
//
// El payload lleva TODO lo que hace falta para volver a componer el texto
// (`code`, `serverMessage`, `retryAfterMinutes`), no solo el texto ya compuesto.
// Es lo que hace inofensivo el único doble envoltorio que queda:
// `dispatch(adoptarSesion()).unwrap()` lanza este payload (no el AuthError), y
// signIn/verifyTotp lo vuelven a pasar por aquí. Con los tres campos presentes,
// `rechazo(rechazo(e))` devuelve un objeto idéntico a `rechazo(e)` — o sea que
// propagarlo tal cual y re-envolverlo son lo mismo. Antes se perdían
// `serverMessage` y `retryAfterMinutes`, y un BLACKLISTED de /session tras un
// login correcto mostraba el texto de catálogo en vez del del server (§3.4).
function rechazo(e, contexto) {
  return {
    message: mensajeDeError(e, contexto),
    code: (e && e.code) || '',
    serverMessage: (e && e.serverMessage) || '',
    retryAfterMinutes: e && e.retryAfterMinutes,
  };
}

// Tras login/verifyTotp/activate (que ya dejaron el token en authClient) o tras
// boot con cookie válida: deja token y usuario en el estado.
//
// El usuario sale de authClient si ya lo tiene: /login, /refresh, /mfa/totp y
// /mfa/totp/activate lo devuelven junto con el token, así que en boot y en cada
// login se ahorra una ida y vuelta —y, sobre todo, un /session caído deja de
// poder tirar una sesión que ya está viva. /session queda como respaldo: F5 con
// un token que llegó de otra pestaña, o una respuesta que no trajo usuario.
export const adoptarSesion = createAsyncThunk(
  'auth/adoptarSesion',
  async (_, { rejectWithValue }) => {
    try {
      const enMemoria = authClient.estado().user;
      // Sin `silencioso`: cuando toca pedirlo, este es el fetch principal de la
      // sesión y necesita el refresh+retry ante 401 TOKEN_EXPIRED y la entrega
      // de códigos de sesión a recibirCodigo (spec §3.1).
      const datos = enMemoria
        ? { user: enMemoria }
        : await authClient.session();
      const user = usuarioDeSesion(datos);
      try {
        localStorage.setItem(AUTH_USER, JSON.stringify(user));
      } catch (e) {
        // Safari privado o storage lleno: el usuario ya está en memoria y la
        // sesión sigue viva; solo se pierde la rehidratación tras un F5.
      }
      return { token: authClient.getAccessToken(), user };
    } catch (e) {
      // Se propaga el texto y el code: quien lo llama (signIn, verifyTotp,
      // enrolar, sesionRedux.arrancar) decide qué hacer. Ninguno cierra la
      // sesión de authClient por un fallo transitorio de /session — de eso ya
      // se encarga el propio authClient cuando el code es de revocación.
      return rejectWithValue(rechazo(e, 'login'));
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ company, email, password }, { dispatch, rejectWithValue }) => {
    try {
      const r = await authClient.login(email, password, company);
      if (r && r.mfaRequired)
        return { mfaRequired: true, mfaToken: r.mfaToken };
      if (r && r.enrollmentRequired)
        return {
          enrollmentRequired: true,
          enrollmentToken: r.enrollmentToken,
        };
      await dispatch(adoptarSesion()).unwrap();
      return { session: true };
    } catch (e) {
      return rejectWithValue(rechazo(e, 'login'));
    }
  }
);

export const verifyTotp = createAsyncThunk(
  'auth/verifyTotp',
  async ({ mfaToken, totpCode }, { dispatch, rejectWithValue }) => {
    try {
      await authClient.verifyTotp(mfaToken, totpCode);
      await dispatch(adoptarSesion()).unwrap();
      return { session: true };
    } catch (e) {
      return rejectWithValue(rechazo(e, 'login'));
    }
  }
);

export const signOut = createAsyncThunk('auth/signOut', async () => {
  try {
    await authClient.logout();
  } catch (e) {
    // La sesión local se cierra igual: el server la barre al vencer.
  }
  localStorage.removeItem(AUTH_USER);
});

function cerrado(state) {
  state.loading = false;
  state.token = null;
  state.user = {};
  state.redirect = '/';
}

function fallo(state, action) {
  state.message =
    (action.payload && action.payload.message) || TEXTO_POR_CODIGO.GENERIC;
  state.showMessage = true;
  state.messageId = (state.messageId || 0) + 1;
  state.loading = false;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = '/';
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.messageId = (state.messageId || 0) + 1;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = '';
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      cerrado(state);
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    bootTerminado: (state) => {
      state.booting = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adoptarSesion.fulfilled, (state, action) => {
        state.redirect = '/';
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(signIn.rejected, fallo)
      .addCase(verifyTotp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyTotp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyTotp.rejected, fallo)
      .addCase(signOut.fulfilled, cerrado)
      .addCase(signOut.rejected, cerrado);
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  bootTerminado,
} = authSlice.actions;

export default authSlice.reducer;
